import CorvuDialog from '@corvu/dialog'
import { cn } from '@forest/shared'
import { DialogShell } from '@forest/shared/solid'
import { useQuery } from '@tanstack/solid-query'
import {
  createEffect,
  For,
  Match,
  Show,
  Switch,
  type Accessor,
  type JSX,
} from 'solid-js'
import {
  deepLinkOverlayCover,
  detailOverlayAnimation,
  dialogPanelAnimation,
  panelPresented,
} from '~/lib/dialog-animation'
import type { CatalogueCurrency } from '~/lib/catalogue-price'
import { m } from '~/paraglide/messages'
import {
  treeDetailAssetsQuery,
  type CatalogueProduct,
  type TreeProduct,
} from '~/services/catalogue'
import type { ShopBalances } from '~/store/session-ledger'
import { BalancePills } from './balance-pills'
import { DetailUnlockFooter } from './detail-unlock-footer'
import { DialogCloseButton } from './dialog-buttons'

const durationLabels = [
  '≤55',
  '60–85',
  '90–115',
  `≥120 ${m.very_short_minute_unit_lowercase()}`,
] as const

// Shared card shell for the phase strip so the skeleton keeps exactly the
// loaded strip's footprint.
function PhaseSlot(props: { label: string; children: JSX.Element }) {
  return (
    <li class="flex w-16 shrink-0 flex-col items-center gap-1 p-0.5">
      {props.children}
      <span
        data-testid="tree-detail-duration"
        class="text-xs font-medium leading-3 text-[#888]"
      >
        {props.label}
      </span>
    </li>
  )
}

export function TreeDetail(props: {
  balances: Accessor<ShopBalances | undefined>
  // Whether the launch deep link auto-opened this detail (GLOSSARY.md "Shop
  // launch deep link"). Its overlay is a solid cover over the catalogue
  // rather than the frame's blurred glass.
  fromDeepLink?: boolean
  open: boolean
  onClose: () => void
  // Fires once the open dialog is presented: its assets settled — loaded or
  // failed — every image in the panel decoded, and the panel's enter
  // animation finished. The launch deep link's Shop reveal waits on it
  // (GLOSSARY.md "Reveal surface"). Once per mount.
  onPresented?: () => void
  onPurchase: (product: CatalogueProduct) => void
  onTopUp: (currency: CatalogueCurrency) => void
  product: TreeProduct
  serverNow: Accessor<number>
  // A free unlock in flight (GLOSSARY.md "Free product"): the detail is its
  // pending surface, so it cannot be dismissed until the command settles.
  unlockPending?: boolean
}) {
  // Detail assets hydrate on demand (ADR 0020): the dialog opens on grid
  // data alone; only the description and phase strip wait on this query. A
  // launch deep link prefetches its target's assets under the same key
  // (ADR 0024), so that dialog finds them already in flight or settled.
  const assets = useQuery(() =>
    treeDetailAssetsQuery(props.product.gid, props.product.phaseCount),
  )

  let panel: HTMLElement | undefined
  let presented = false
  createEffect(() => {
    if (presented || !props.open) return
    if (assets.data === undefined && !assets.isError) return
    presented = true
    const onPresented = props.onPresented
    if (!onPresented) return
    // The DOM already holds the settled state — the description and phase
    // strip, or the inline retry — so the panel's images are the ones the
    // Shop reveal would land on.
    const element = panel
    if (!element) {
      onPresented()
      return
    }
    void panelPresented(element).then(onPresented)
  })

  return (
    <DialogShell
      ref={(element) => {
        panel = element
      }}
      open={props.open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !props.unlockPending) props.onClose()
      }}
      closeOnEscapeKeyDown={false}
      testId="tree-detail"
      class={`z-30 flex w-[300px] flex-col items-center gap-4 p-6 text-center text-[#333] outline-none ${dialogPanelAnimation}`}
      overlayClass={cn(
        detailOverlayAnimation,
        props.fromDeepLink && deepLinkOverlayCover,
      )}
      overlayContent={
        <BalancePills
          balances={props.balances()}
          class="fixed right-2 top-[calc(8px+var(--safe-area-top))] z-[31] duration-300 animate-in fade-in"
          inverted
          testIdPrefix="tree-detail-"
        />
      }
    >
      <>
        <DialogCloseButton
          disabled={!props.open || props.unlockPending}
          onClose={() => props.onClose()}
        />

        <div class="flex w-full flex-col items-center gap-2">
          <img
            data-testid="tree-detail-hero"
            src={props.product.productImage.src}
            alt=""
            class="h-[168px] w-full object-contain"
          />
          <div class="flex w-full flex-col items-center gap-2">
            <CorvuDialog.Label
              id="tree-detail-title"
              class="w-full text-base font-semibold leading-[26px]"
            >
              {props.product.title}
            </CorvuDialog.Label>
            <Show
              when={assets.data}
              fallback={
                <Show when={!assets.isError || assets.isFetching}>
                  <div
                    aria-hidden="true"
                    class="h-[22px] w-3/4 animate-pulse rounded-full bg-black/10"
                  />
                </Show>
              }
            >
              {(data) => (
                <CorvuDialog.Description class="w-full text-sm leading-[22px] text-[#666]">
                  {data().description}
                </CorvuDialog.Description>
              )}
            </Show>
          </div>
        </div>

        <div class="flex w-full flex-col items-center gap-6">
          {/* A four-phase tree's count came with its card (ADR 0050): no
              strip, so no skeleton holding a place the loaded dialog drops. */}
          <Switch
            fallback={
              <Show when={props.product.phaseCount !== 4}>
                <ul
                  data-testid="tree-detail-assets-skeleton"
                  aria-hidden="true"
                  class="flex items-center"
                >
                  <For each={durationLabels}>
                    {(label) => (
                      <PhaseSlot label={label}>
                        <div class="size-16 shrink-0 animate-pulse rounded-2xl bg-black/10" />
                      </PhaseSlot>
                    )}
                  </For>
                </ul>
              </Show>
            }
          >
            <Match when={assets.data}>
              {(data) => (
                // A four-phase tree (null phaseImages) renders no strip. One
                // whose count was unknown on open loses the skeleton's
                // section here.
                <Show when={data().phaseImages}>
                  {(phaseImages) => (
                    <ul
                      data-testid="tree-detail-phases"
                      class="flex items-center"
                    >
                      <For each={phaseImages()}>
                        {(image, index) => (
                          <PhaseSlot label={durationLabels[index()]!}>
                            <img
                              data-testid="tree-detail-preview"
                              src={image.src}
                              alt=""
                              class="size-16 max-w-none shrink-0 object-contain"
                            />
                          </PhaseSlot>
                        )}
                      </For>
                    </ul>
                  )}
                </Show>
              )}
            </Match>
            <Match when={assets.isError && !assets.isFetching}>
              <div
                data-testid="tree-detail-assets-error"
                class="flex min-h-[84px] w-full flex-col items-center justify-center gap-2"
              >
                <span class="text-sm leading-[22px] text-[#666]">
                  {m.dialog_something_went_wrong_title()}
                </span>
                <button
                  type="button"
                  data-testid="tree-detail-assets-retry"
                  onClick={() => void assets.refetch()}
                  class="rounded-full bg-white/60 px-4 py-1.5 text-sm font-semibold leading-4 text-[#176361]"
                >
                  {m.retry()}
                </button>
              </div>
            </Match>
          </Switch>

          <DetailUnlockFooter
            balances={props.balances}
            onPurchase={props.onPurchase}
            onTopUp={props.onTopUp}
            pending={props.unlockPending}
            product={props.product}
            serverNow={props.serverNow}
            testIdPrefix="tree-detail"
          />
        </div>
      </>
    </DialogShell>
  )
}
